// Site-gate invites — the "Site invites" tab of /internal/signups.
//
// Per-person links past vera.inc's pre-launch password gate
// (`vera.inc/enter?invite=<token>`). Lives beside Waitlist and Allowlist because it
// answers the same question for an earlier door: who may see the site at all, before
// anyone may sign up for the platform. API: apps/api/src/features/site-gate-invites.
//
// ⚠️ THE LINK IS SHOWN ONCE. The API stores only a SHA-256 of the token, so after the
// "Invite link" dialog closes nothing can reconstruct it. Lost link → revoke it and
// create another. The dialog says so, because the alternative is someone closing it
// expecting a "copy link" item on the row that cannot exist.
import React, { useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Box, IconButton, Stack, Typography } from '@mui/material'
import { BanIcon, CheckIcon, CopyIcon, KeyRoundIcon } from 'lucide-react'
import { Alert, Button, EMPTY_VALUE, Input, Modal, TextField } from '@repo/ui'

import { ActionDropdown, ConfirmSettingModal, EmptyState, SelectField, StatusChip, Table } from 'components'
import { SITE_GATE_INVITE_STATUSES } from 'constants/statuses'
import { useSnackbar } from 'contexts'
import { date, getErrorMessage } from 'utils'
import {
  useCreateSiteGateInvite,
  useRevokeSiteGateInvite,
  useSiteGateInvites,
  type CreatedSiteGateInvite,
  type SiteGateInvite,
} from 'hooks/api/useSiteGateInvites'

const cellFlexSx = { display: 'flex', alignItems: 'center', height: '100%', minWidth: 0 }

const EXPIRY_OPTIONS = [
  { label: '7 days', value: '7' },
  { label: '14 days', value: '14' },
  { label: '30 days', value: '30' },
  { label: 'Custom date', value: 'custom' },
]

const DAY_MS = 24 * 60 * 60 * 1000
/** Mirrors the API's SITE_GATE_INVITE_MAX_DAYS. The API is the boundary; this only
 *  stops the picker offering a date the API will refuse. */
const MAX_DAYS = 90

/** `YYYY-MM-DD` for a date input, in local time. */
const toDateInput = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

/** A custom date means "through the end of that day", locally — an invite set to
 *  expire "on the 14th" that died at midnight on the 13th/14th boundary would be
 *  a day short. */
const endOfLocalDay = (yyyyMmDd: string): Date => {
  const [y, m, d] = yyyyMmDd.split('-').map(Number)
  return new Date(y, m - 1, d, 23, 59, 59)
}

type FormData = {
  label: string
  email: string
  note: string
  expiry: string
  customDate: string
}

const CreateInviteModal = ({
  open,
  onClose,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  onCreated: (invite: CreatedSiteGateInvite) => void
}) => {
  const { open: notification } = useSnackbar()
  const { control, handleSubmit, reset, watch } = useForm<FormData>({
    defaultValues: { label: '', email: '', note: '', expiry: '14', customDate: '' },
  })
  const expiry = watch('expiry')
  const today = new Date()
  const minDate = toDateInput(new Date(today.getTime() + DAY_MS))
  const maxDate = toDateInput(new Date(today.getTime() + MAX_DAYS * DAY_MS))

  const { mutate: create, isPending } = useCreateSiteGateInvite({
    onSuccess: (invite) => {
      reset()
      onCreated(invite)
    },
    onError: (error) => notification({ type: 'error', title: getErrorMessage(error) }),
  })

  const handleClose = () => {
    reset()
    onClose()
  }

  const onSubmit = handleSubmit((data) => {
    const expiresAt =
      data.expiry === 'custom'
        ? endOfLocalDay(data.customDate)
        : new Date(Date.now() + Number(data.expiry) * DAY_MS)
    create({
      label: data.label.trim(),
      email: data.email.trim() || undefined,
      note: data.note.trim() || undefined,
      expires_at: expiresAt.toISOString(),
    })
  })

  return (
    <Modal
      title="Create site invite"
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" form="create-site-invite-form" loading={isPending}>
            Create link
          </Button>
        </>
      }
    >
      <Stack component="form" id="create-site-invite-form" onSubmit={onSubmit} spacing={4} mt={2}>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          A personal link that lets one person past the vera.inc password screen until it expires
          or you revoke it. Nothing is emailed; you copy the link and send it yourself.
        </Typography>

        <Stack spacing={2}>
          <Typography variant="body2" sx={{ fontSize: 16 }}>
            Name
          </Typography>
          <Controller
            name="label"
            control={control}
            rules={{ required: 'Name is required', maxLength: { value: 120, message: 'Keep it under 120 characters' } }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                placeholder="Jane Investor"
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Stack>

        <Stack spacing={2}>
          <Typography variant="body2" sx={{ fontSize: 16 }}>
            Email{' '}
            <Typography component="span" variant="body2" color="text.secondary">
              (optional)
            </Typography>
          </Typography>
          <Controller
            name="email"
            control={control}
            rules={{ pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' } }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                type="email"
                placeholder="jane@fund.com"
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Stack>

        <Stack spacing={2}>
          <Typography variant="body2" sx={{ fontSize: 16 }}>
            Expires
          </Typography>
          <Controller
            name="expiry"
            control={control}
            render={({ field }) => (
              <SelectField value={field.value} onChange={(v) => field.onChange(v as string)} options={EXPIRY_OPTIONS} />
            )}
          />
          {expiry === 'custom' && (
            <Controller
              name="customDate"
              control={control}
              rules={{
                validate: (v) => {
                  if (!v) return 'Pick a date'
                  if (v < minDate || v > maxDate) return `Pick a date between tomorrow and ${MAX_DAYS} days from now`
                  return true
                },
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  type="date"
                  inputProps={{ min: minDate, max: maxDate }}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          )}
        </Stack>

        <Stack spacing={2}>
          <Typography variant="body2" sx={{ fontSize: 16 }}>
            Note{' '}
            <Typography component="span" variant="body2" color="text.secondary">
              (optional, staff only)
            </Typography>
          </Typography>
          <Controller
            name="note"
            control={control}
            rules={{ maxLength: { value: 500, message: 'Keep it under 500 characters' } }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                placeholder="Seed round, met at the Paris show"
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Stack>
      </Stack>
    </Modal>
  )
}

/** The once-only reveal. */
const InviteLinkModal = ({ invite, onClose }: { invite: CreatedSiteGateInvite | null; onClose: () => void }) => {
  const [copied, setCopied] = useState(false)
  const handleCopy = () => {
    if (!invite) return
    void navigator.clipboard.writeText(invite.url)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1500)
  }
  return (
    <Modal
      title="Invite link"
      open={Boolean(invite)}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Done
          </Button>
          <Button onClick={handleCopy} startIcon={copied ? <CheckIcon strokeWidth={1} /> : <CopyIcon strokeWidth={1} />}>
            {copied ? 'Copied' : 'Copy link'}
          </Button>
        </>
      }
    >
      <Stack spacing={4} mt={2}>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          Send this to <strong>{invite?.label}</strong>. It opens the site without the password until{' '}
          {invite ? date(invite.expires_at) : ''}.
        </Typography>
        <Input
          label="Link"
          value={invite?.url ?? ''}
          readOnly
          endAdornment={
            <IconButton aria-label="Copy invite link" onClick={handleCopy} size="small">
              {copied ? <CheckIcon strokeWidth={1} size={18} /> : <CopyIcon strokeWidth={1} size={18} />}
            </IconButton>
          }
        />
        <Alert
          severity="warning"
          description="This is the only time the link is shown. Only a fingerprint of it is stored, so it cannot be copied again later. If it is lost, revoke it and create a new one."
        />
      </Stack>
    </Modal>
  )
}

const lastUsedDetail = (row: SiteGateInvite): string | null => {
  const parts = [row.last_used_ua, row.last_used_country].filter(Boolean)
  return parts.length ? parts.join(' · ') : null
}

export const SiteInvitesTab = ({ createOpen, onCloseCreate }: { createOpen: boolean; onCloseCreate: () => void }) => {
  const { open: notification } = useSnackbar()
  const { data: invites = [], isLoading } = useSiteGateInvites()
  const [created, setCreated] = useState<CreatedSiteGateInvite | null>(null)
  const [revokeTarget, setRevokeTarget] = useState<SiteGateInvite | null>(null)

  const { mutate: revoke, isPending: isRevoking } = useRevokeSiteGateInvite({
    onSuccess: (row) => notification({ type: 'success', title: `Invite for ${row.label} revoked` }),
    onError: (error) => notification({ type: 'error', title: getErrorMessage(error) }),
  })

  const columns = useMemo(
    () => [
      {
        field: 'label',
        type: 'string',
        headerName: 'Name',
        width: 260,
        minWidth: 220,
        flex: 260,
        sortable: true,
        disableColumnMenu: true,
        pinned: 'left',
        renderCell: ({ row }: { row: SiteGateInvite }) => (
          <Stack justifyContent="center" sx={{ height: '100%', minWidth: 0 }}>
            <Typography variant="body2" noWrap title={row.note ?? undefined}>
              {row.label}
            </Typography>
            {row.email && (
              <Typography variant="body3" color="text.secondary" noWrap>
                {row.email}
              </Typography>
            )}
          </Stack>
        ),
      },
      {
        field: 'status',
        type: 'string',
        headerName: 'Status',
        width: 130,
        minWidth: 130,
        flex: 130,
        sortable: true,
        disableColumnMenu: true,
        renderCell: ({ row }: { row: SiteGateInvite }) => (
          <Box sx={cellFlexSx}>
            <StatusChip size="small" statuses={SITE_GATE_INVITE_STATUSES} status={row.status} />
          </Box>
        ),
      },
      {
        field: 'last_used_at',
        type: 'dateTime',
        headerName: 'Last used',
        width: 220,
        minWidth: 200,
        flex: 220,
        sortable: true,
        disableColumnMenu: true,
        valueGetter: (value: unknown) => (value ? new Date(value as string) : null),
        renderCell: ({ row }: { row: SiteGateInvite }) => (
          <Stack justifyContent="center" sx={{ height: '100%', minWidth: 0 }}>
            <Typography variant="body2" color={row.last_used_at ? 'text.secondary' : 'text.disabled'}>
              {row.last_used_at ? date(row.last_used_at, 'MMM dd yyyy, HH:mm') : EMPTY_VALUE}
            </Typography>
            {lastUsedDetail(row) && (
              <Typography variant="body3" color="text.secondary" noWrap>
                {lastUsedDetail(row)}
              </Typography>
            )}
          </Stack>
        ),
      },
      {
        field: 'use_count',
        headerName: 'Opens',
        type: 'number',
        width: 104,
        minWidth: 104,
        flex: 104,
        sortable: true,
        disableColumnMenu: true,
        align: 'left',
        headerAlign: 'left',
        renderCell: ({ row }: { row: SiteGateInvite }) => (
          <Box sx={cellFlexSx}>
            <Typography variant="body2" color="text.secondary">
              {row.use_count}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'expires_at',
        type: 'dateTime',
        headerName: 'Expires',
        width: 140,
        minWidth: 140,
        flex: 140,
        sortable: true,
        disableColumnMenu: true,
        valueGetter: (value: unknown) => (value ? new Date(value as string) : null),
        renderCell: ({ row }: { row: SiteGateInvite }) => (
          <Box sx={cellFlexSx}>
            <Typography variant="body2" color="text.secondary">
              {date(row.expires_at)}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'created_at',
        type: 'dateTime',
        headerName: 'Created',
        width: 140,
        minWidth: 140,
        flex: 140,
        sortable: true,
        disableColumnMenu: true,
        valueGetter: (value: unknown) => (value ? new Date(value as string) : null),
        renderCell: ({ row }: { row: SiteGateInvite }) => (
          <Box sx={cellFlexSx}>
            <Typography variant="body2" color="text.secondary">
              {date(row.created_at)}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'actions',
        headerName: '',
        width: 54,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        disableReorder: true,
        pinned: 'right',
        // One action. "Copy link" is deliberately absent: the token is not stored, so
        // there is nothing to copy (see the file header).
        renderCell: ({ row }: { row: SiteGateInvite }) => (
          <ActionDropdown
            items={[
              {
                label: 'Revoke',
                icon: <BanIcon strokeWidth={1} />,
                destructive: true,
                disabled: row.status === 'revoked' || row.status === 'expired',
                tooltip:
                  row.status === 'revoked'
                    ? 'Already revoked'
                    : row.status === 'expired'
                      ? 'Already expired'
                      : undefined,
                onClick: () => setRevokeTarget(row),
              },
            ]}
          />
        ),
      },
    ],
    []
  )

  return (
    <>
      <Table
        loading={isLoading}
        columns={columns}
        rows={invites}
        alwaysShowRowSelection={false}
        fillBottom
        paginationMode="client"
        initialPageSize={100}
        emptyState={
          <EmptyState
            title="No site invites yet"
            text="Create a personal link that lets someone past the vera.inc password screen."
            icon={<KeyRoundIcon strokeWidth={1} size={32} />}
          />
        }
      />

      <CreateInviteModal
        open={createOpen}
        onClose={onCloseCreate}
        onCreated={(invite) => {
          onCloseCreate()
          setCreated(invite)
        }}
      />

      <InviteLinkModal invite={created} onClose={() => setCreated(null)} />

      <ConfirmSettingModal
        open={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        onConfirm={() => {
          if (revokeTarget) revoke({ id: revokeTarget.id })
          setRevokeTarget(null)
        }}
        loading={isRevoking}
        destructive
        title="Revoke this invite?"
        confirmLabel="Revoke"
        consequence={
          <>
            The link for <strong>{revokeTarget?.label}</strong> stops working now. If they have already
            used it, their browser keeps access until its next daily check, at most 24 hours.
          </>
        }
      />
    </>
  )
}
